import { useEffect, useState } from 'react';
import { centerPerfAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS } from '../../utils/uiStyles';
import { fmtKstDateTime } from '../../utils/kstTime';

/* 센터 실적 «기준 데이터 → 미연결 고객사명» — 업로드 원문 고객사명을 고객사 마스터 표준명에 잇는다.
 * · 목록은 서버가 정한다(/center-perf/names/unlinked·links — 조회 범위 안 실적 센터만). 원문이 마스터 표준명과 같으면
 *   서버가 자동으로 이어 미연결 목록에 넣지 않는다.
 * · canManage(운영·시스템 관리자·기준 데이터 관리 조직)면 행마다 표준명 칸·«찾기»(고객사 마스터 검색 — 저장된 연도 전부)·«연결».
 *   서버가 권한과 «마스터에 있는 표준명인가»를 다시 본다(아니면 400 문구를 그대로 보인다). 저장된 업로드 원문은 바뀌지 않는다.
 * · 연결은 같은 실적 센터·같은 원문으로 나중에 올린 업로드에도 적용된다. «연결 해제»하면 그 원문은 다시 미연결로 돌아간다.
 */
const SEARCH_LIMIT = 20;

const card = { background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' };
const th = { padding: '7px 9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '5px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top' };
const tdNum = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
const inp = { padding: '4px 6px', border: `1px solid ${COLORS.inputBorder}`, borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' };
const btn = (bg, color = '#fff') => ({ padding: '5px 10px', background: bg, color, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 });
const hint = { fontSize: '12px', color: COLORS.textMuted, marginBottom: '8px' };
const chip = { padding: '1px 8px', border: `1px solid ${COLORS.borderSoft}`, borderRadius: '999px', background: COLORS.surface, color: COLORS.text, cursor: 'pointer', fontSize: '12px' };

const detailOf = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' && d ? d : fallback;
};
const fmtNum = (v) => Number(v || 0).toLocaleString('ko-KR');
const rowKey = (u) => `${u.perf_center}|${u.raw_name}`;

const fetchBoth = (center) => Promise.all([centerPerfAPI.refUnlinked(center), centerPerfAPI.refLinks(center)])
  .then(([u, l]) => [u.data?.unlinked || [], l.data?.links || []]);

// 고객사 마스터에서 표준명 후보 찾기 — 서버가 고른 최근 연도 먼저, 나머지 저장 연도도 같은 검색어로(표준명은 전 연도라서).
// 중복 없이 앞 SEARCH_LIMIT 개. 다른 연도 검색이 실패하면 그 연도만 빠진다.
export async function searchStandardNames(q) {
  const first = (await centerPerfAPI.refCustomers(undefined, q)).data || {};
  const others = (first.years || []).filter((y) => y !== first.year);
  const rest = await Promise.all(others.map((y) => centerPerfAPI.refCustomers(y, q)
    .then((r) => r.data || {})
    .catch(() => ({}))));
  const out = [];
  [first, ...rest].forEach((d) => (d.customers || []).forEach((c) => {
    const name = (c.biz_name || '').trim();
    if (name && !out.includes(name)) out.push(name);
  }));
  return out.slice(0, SEARCH_LIMIT);
}

export default function CustomerLinkSection({ centers = [], canManage = false }) {
  const [center, setCenter] = useState('');            // '' = 조회 범위 전부
  const [unlinked, setUnlinked] = useState(null);
  const [links, setLinks] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [msg, setMsg] = useState(null);                // { tone: 'ok' | 'err', text }
  const [busy, setBusy] = useState(false);
  const [inputs, setInputs] = useState({});            // rowKey → 표준명 입력
  const [options, setOptions] = useState({});          // rowKey → 찾은 표준명 후보(null = 아직 안 찾음)

  useEffect(() => {
    let alive = true;
    setLoadError('');
    fetchBoth(center)
      .then(([u, l]) => { if (alive) { setUnlinked(u); setLinks(l); } })
      .catch((e) => { if (alive) { setUnlinked(null); setLinks(null); setLoadError(detailOf(e, '고객사명 연결을 불러오지 못했습니다.')); } });
    return () => { alive = false; };
  }, [center]);

  const refresh = async (okText) => {
    try {
      const [u, l] = await fetchBoth(center);
      setUnlinked(u);
      setLinks(l);
      setMsg({ tone: 'ok', text: okText });
    } catch (e) {
      setMsg({ tone: 'err', text: detailOf(e, '목록을 다시 불러오지 못했습니다.') });
    }
  };

  const forget = (key) => {
    setInputs((p) => { const n = { ...p }; delete n[key]; return n; });
    setOptions((p) => { const n = { ...p }; delete n[key]; return n; });
  };

  const find = async (u) => {
    const key = rowKey(u);
    const q = (inputs[key] || '').trim();
    if (!q) return;
    setBusy(true);
    setMsg(null);
    try {
      const names = await searchStandardNames(q);
      setOptions((p) => ({ ...p, [key]: names }));
    } catch (e) {
      setMsg({ tone: 'err', text: detailOf(e, '고객사 마스터를 찾지 못했습니다.') });
    } finally {
      setBusy(false);
    }
  };

  const link = async (u) => {
    const key = rowKey(u);
    const std = (inputs[key] || '').trim();
    if (!std) return;
    setBusy(true);
    setMsg(null);
    try {
      let saved;
      try {
        const r = await centerPerfAPI.saveLink({ perf_center: u.perf_center, raw_name: u.raw_name, standard_name: std });
        saved = r.data?.link?.standard_name || std;
      } catch (e) {
        setMsg({ tone: 'err', text: detailOf(e, '연결하지 못했습니다.') });
        return;
      }
      forget(key);
      await refresh(`'${u.raw_name}' 을(를) '${saved}' 에 연결했습니다`);
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (lk) => {
    const ok = await confirmDialog(
      `'${lk.raw_name}' → '${lk.standard_name}' 연결을 해제합니다. 저장된 업로드 원문은 그대로이고, 이 원문은 다시 미연결로 돌아갑니다.`,
      { danger: true },
    );
    if (!ok) return;
    setBusy(true);
    setMsg(null);
    try {
      try {
        await centerPerfAPI.removeLink(lk.id);
      } catch (e) {
        setMsg({ tone: 'err', text: detailOf(e, '연결을 해제하지 못했습니다.') });
        return;
      }
      await refresh(`'${lk.raw_name}' 연결을 해제했습니다`);
    } finally {
      setBusy(false);
    }
  };

  const unlinkedHeaders = ['실적 센터', '원문 고객사명', '행 수', '마지막 날짜', ...(canManage ? ['표준명', ''] : [])];
  const linkHeaders = ['실적 센터', '원문 고객사명', '표준명', '연결 시각', ...(canManage ? [''] : [])];

  return (
    <div style={card}>
      <div style={hint}>
        업로드 원문 고객사명 중 고객사 마스터 표준명(사업자등록증 명칭)과 다른 것입니다. 같으면 자동으로 이어집니다.
        연결하면 같은 실적 센터·같은 원문으로 나중에 올린 업로드에도 적용되고, 저장된 원문은 바뀌지 않습니다.
        {canManage ? '' : ' 이 화면은 읽기 전용입니다.'}
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
        <select aria-label="연결 실적 센터" value={center} style={{ ...inp, fontSize: '13px' }}
          onChange={(e) => setCenter(e.target.value)}>
          <option value="">전체(조회 범위)</option>
          {centers.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {loadError && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px', marginBottom: '8px' }}>{loadError}</div>}
      {msg && (
        <div role={msg.tone === 'err' ? 'alert' : 'status'}
          style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: msg.tone === 'err' ? COLORS.dangerText : COLORS.successText }}>
          {msg.text}
        </div>
      )}

      <div style={{ fontWeight: 700, fontSize: '13px', margin: '4px 0 6px' }}>미연결 고객사명</div>
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="perf-unlinked" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>{unlinkedHeaders.map((h, i) => <th key={h || `act-${i}`} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {unlinked && unlinked.length === 0 && (
              <tr><td colSpan={unlinkedHeaders.length} style={{ ...td, color: COLORS.textMuted }}>미연결 고객사명이 없습니다.</td></tr>
            )}
            {(unlinked || []).map((u) => {
              const key = rowKey(u);
              const value = inputs[key] || '';
              const found = options[key];
              return (
                <tr key={key} data-raw={u.raw_name} data-center={u.perf_center}>
                  <td data-col="perf_center" style={{ ...td, whiteSpace: 'nowrap' }}>{u.perf_center}</td>
                  <td data-col="raw_name" style={{ ...td, fontWeight: 600 }}>{u.raw_name}</td>
                  <td data-col="rows" style={tdNum}>{fmtNum(u.rows)}</td>
                  <td data-col="last_date" style={{ ...td, whiteSpace: 'nowrap' }}>{u.last_date || '—'}</td>
                  {canManage && (
                    <td data-col="standard" style={td}>
                      <span style={{ display: 'inline-flex', gap: '4px' }}>
                        <input aria-label={`${u.raw_name} 표준명`} value={value} maxLength={300} placeholder="표준명 검색·입력"
                          style={{ ...inp, width: '180px' }}
                          onChange={(e) => setInputs((p) => ({ ...p, [key]: e.target.value }))} />
                        <button type="button" aria-label={`${u.raw_name} 표준명 찾기`} disabled={busy || !value.trim()}
                          onClick={() => find(u)} style={btn(COLORS.surface, COLORS.text)}>찾기</button>
                      </span>
                      {Array.isArray(found) && (
                        <div data-testid="perf-link-options" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                          {found.length === 0 && <span style={{ fontSize: '12px', color: COLORS.textMuted }}>찾은 표준명이 없습니다.</span>}
                          {found.map((name) => (
                            <button key={name} type="button" aria-label={`${u.raw_name} 표준명 후보 ${name}`} style={chip}
                              onClick={() => setInputs((p) => ({ ...p, [key]: name }))}>{name}</button>
                          ))}
                        </div>
                      )}
                    </td>
                  )}
                  {canManage && (
                    <td style={td}>
                      <button type="button" aria-label={`${u.raw_name} 연결`} disabled={busy || !value.trim()}
                        onClick={() => link(u)} style={{ ...btn(COLORS.primary), opacity: busy || !value.trim() ? 0.5 : 1 }}>연결</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontWeight: 700, fontSize: '13px', margin: '14px 0 6px' }}>연결 목록</div>
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="perf-links" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>{linkHeaders.map((h, i) => <th key={h || `act-${i}`} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {links && links.length === 0 && (
              <tr><td colSpan={linkHeaders.length} style={{ ...td, color: COLORS.textMuted }}>연결한 고객사명이 없습니다.</td></tr>
            )}
            {(links || []).map((lk) => (
              <tr key={lk.id} data-raw={lk.raw_name} data-center={lk.perf_center} data-link-id={lk.id}>
                <td data-col="perf_center" style={{ ...td, whiteSpace: 'nowrap' }}>{lk.perf_center}</td>
                <td data-col="raw_name" style={td}>{lk.raw_name}</td>
                <td data-col="standard_name" style={{ ...td, fontWeight: 600 }}>{lk.standard_name}</td>
                <td data-col="updated_at" style={{ ...td, whiteSpace: 'nowrap', color: COLORS.textSub }}>
                  {fmtKstDateTime(lk.updated_at || lk.created_at) || '—'}
                </td>
                {canManage && (
                  <td style={td}>
                    <button type="button" aria-label={`${lk.raw_name} 연결 해제`} disabled={busy} onClick={() => unlink(lk)}
                      style={btn(COLORS.dangerSoft, COLORS.dangerText)}>연결 해제</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
